// 🗂️ Advanced Sidebar Component with Navigation
// React Patterns: React.memo + Animation + Active States + Responsive Design

import React, { memo, useEffect, useMemo, useState } from 'react';
import { useUI } from '../store/AppContext';
import ApiService from '../services/api';
import { canAccessMenu, filterSidebarSectionsByAcl, getFirstAllowedMenu } from '../security/acl';
import { useAdminNavigate } from '../navigation/useAdminNavigate';
import { menuIdToPath, MENU_TO_PATH } from '../navigation/adminRoutes';
import { useNavigate } from 'react-router-dom';
import { SIDEBAR_SECTIONS, flattenSidebarMenuIds } from './sidebarMenuConfig';

const SectionLabel = memo(({ label, isCollapsed }) => {
  if (!label || isCollapsed) return null;
  return (
    <p className="px-4 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
      {label}
    </p>
  );
});
SectionLabel.displayName = 'SectionLabel';


// 🎯 Menu Item Component
const resolveMenuId = (item) => item?.menuTargetId || item?.id;

const MenuItem = memo(({ item, isActive, isCollapsed, onClick }) => {
  const handleClick = () => {
    onClick(resolveMenuId(item));
  };

  const rowClass = `
        w-full flex items-center px-4 py-2.5 text-left transition-all duration-200 group relative
        ${isActive
          ? 'bg-indigo-600 text-white shadow-sm'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
        }
        ${isCollapsed ? 'justify-center' : 'justify-start'}
      `;

  const inner = (
    <>
      {/* Active Indicator */}
      {isActive && !item.externalUrl && (
        <div className="absolute left-0 top-1 bottom-1 w-1 bg-white rounded-r-full"></div>
      )}

      {/* Icon */}
      <div className={`
        flex-shrink-0 w-6 h-6 flex items-center justify-center relative
        ${isCollapsed ? '' : 'mr-3'}
      `}>
        <i className={`${item.icon} text-lg`}></i>

        {item.badge && (
          <span className={`
            absolute -top-1 -right-1 w-4 h-4 text-xs text-white rounded-full flex items-center justify-center
            ${item.badgeColor || 'bg-red-500'}
            ${isCollapsed ? 'scale-75' : ''}
          `}>
            {item.badge}
          </span>
        )}
      </div>

      {!isCollapsed && (
        <div className="flex-1 min-w-0 flex items-center gap-1">
          <div className="font-medium truncate">{item.label}</div>
          {item.externalUrl ? (
            <i className="fas fa-external-link-alt text-[10px] opacity-60 shrink-0" aria-hidden />
          ) : null}
        </div>
      )}

      {isCollapsed && (
        <div className="
          absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-sm rounded-lg
          opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
          whitespace-nowrap z-50
        ">
          {item.label}
        </div>
      )}
    </>
  );

  if (item.externalUrl) {
    return (
      <a
        href={item.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${rowClass} no-underline`}
        title={isCollapsed ? item.label : item.description || ''}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={rowClass}
      title={isCollapsed ? item.label : ''}
    >
      {inner}
    </button>
  );
});

MenuItem.displayName = 'MenuItem';

function groupHasActiveDescendant(group, activeMenu) {
  if (!group?.children?.length) return false;
  return group.children.some((child) => {
    if (child.isGroup && child.children?.length) {
      return groupHasActiveDescendant(child, activeMenu);
    }
    return child.id === activeMenu || resolveMenuId(child) === activeMenu;
  });
}

const MenuSubGroup = memo(({ group, activeMenu, onSelectChild, depth = 1 }) => {
  const hasActiveChild = groupHasActiveDescendant(group, activeMenu);
  const [expanded, setExpanded] = useState(hasActiveChild);
  const padClass = depth === 1 ? 'pl-11' : 'pl-14';

  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`
          relative w-full flex items-center ${padClass} pr-4 py-2 text-left text-sm transition-all duration-200
          ${hasActiveChild ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
        `}
      >
        {hasActiveChild && (
          <div className="absolute left-0 top-1 bottom-1 w-1 bg-indigo-400 rounded-r-full" />
        )}
        <i className={`${group.icon} w-5 text-center mr-2 opacity-80`} />
        <span className="flex-1 min-w-0 truncate">{group.label}</span>
        <i className={`fas fa-chevron-${expanded ? 'down' : 'right'} text-[10px] opacity-60 ml-1`} />
      </button>
      {expanded &&
        group.children.map((child) => {
          if (child.isGroup && child.children?.length) {
            return (
              <MenuSubGroup
                key={child.id}
                group={child}
                activeMenu={activeMenu}
                onSelectChild={onSelectChild}
                depth={depth + 1}
              />
            );
          }
          const isChildActive = activeMenu === child.id || activeMenu === resolveMenuId(child);
          const childPad = depth === 1 ? 'pl-16' : 'pl-[4.5rem]';
          return (
            <button
              key={child.id}
              type="button"
              onClick={() => onSelectChild(resolveMenuId(child))}
              className={`
                relative w-full flex items-center ${childPad} pr-4 py-2 text-left text-sm transition-all duration-200
                ${isChildActive ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
              `}
            >
              {isChildActive && (
                <div className="absolute left-0 top-1 bottom-1 w-1 bg-white rounded-r-full" />
              )}
              <i className={`${child.icon} w-5 text-center mr-2 opacity-80`} />
              <span className="flex-1 min-w-0 truncate">{child.label}</span>
            </button>
          );
        })}
    </div>
  );
});
MenuSubGroup.displayName = 'MenuSubGroup';

const MenuGroup = memo(({ group, activeMenu, isCollapsed, onSelectChild }) => {
  const firstInternalChild = group.children.find((c) => !c.externalUrl && (!c.isGroup || c.children?.length));
  const hasActiveChild = groupHasActiveDescendant(group, activeMenu);
  const [expanded, setExpanded] = useState(hasActiveChild);

  /** Shown next to API docs so admins see which deployment Swagger targets (set `VITE_APP_ENVIRONMENT` to override). */
  const deployEnvLabel = useMemo(() => {
    try {
      const explicit =
        typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_ENVIRONMENT
          ? String(import.meta.env.VITE_APP_ENVIRONMENT).trim()
          : '';
      if (explicit) return explicit.length > 24 ? `${explicit.slice(0, 24)}…` : explicit;
      if (typeof import.meta !== 'undefined' && import.meta.env?.MODE) {
        return String(import.meta.env.MODE).slice(0, 16);
      }
    } catch {
      /* ignore */
    }
    return '';
  }, []);

  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  if (isCollapsed) {
    return (
      <button
        type="button"
        onClick={() => {
          const internal = group.children.find((c) => !c.externalUrl);
          if (internal) onSelectChild(resolveMenuId(internal));
        }}
        className={`
          w-full flex items-center justify-center px-4 py-2.5 text-left transition-all duration-200 group relative
          ${hasActiveChild ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
        `}
        title={
          firstInternalChild ? `${group.label} — ${firstInternalChild.label}` : group.label
        }
      >
        {hasActiveChild && (
          <div className="absolute left-0 top-1 bottom-1 w-1 bg-white rounded-r-full" />
        )}
        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center relative">
          <i className={`${group.icon} text-lg`} />
          {group.badge && (
            <span
              className={`
            absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 text-[10px] leading-4 text-white rounded-full flex items-center justify-center
            ${group.badgeColor || 'bg-red-500'}
            scale-75
          `}
            >
              {group.badge}
            </span>
          )}
        </div>
        <div
          className="
          absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-sm rounded-lg
          opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
          whitespace-nowrap z-50
        "
        >
          {group.label}
        </div>
      </button>
    );
  }

  return (
    <div className="space-y-0">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`
          w-full flex items-center px-4 py-2.5 text-left transition-all duration-200 group relative
          ${hasActiveChild ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
        `}
      >
        {hasActiveChild && (
          <div className="absolute left-0 top-1 bottom-1 w-1 bg-indigo-400 rounded-r-full" />
        )}
        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center relative mr-3">
          <i className={`${group.icon} text-lg`} />
          {group.badge && (
            <span
              className={`
              absolute -top-1 -right-1 min-w-[1rem] h-4 px-0.5 text-[10px] leading-4 text-white rounded-full flex items-center justify-center
              ${group.badgeColor || 'bg-red-500'}
            `}
            >
              {group.badge}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{group.label}</div>
        </div>
        <i className={`fas fa-chevron-${expanded ? 'down' : 'right'} text-xs opacity-60 ml-1`} />
      </button>
      {expanded &&
        group.children.map((child) => {
          if (child.isGroup && child.children?.length) {
            return (
              <MenuSubGroup
                key={child.id}
                group={child}
                activeMenu={activeMenu}
                onSelectChild={onSelectChild}
                depth={1}
              />
            );
          }
          const isChildActive = activeMenu === child.id || activeMenu === resolveMenuId(child);
          const rowClasses = `
                relative w-full flex items-center pl-11 pr-4 py-2 text-left text-sm transition-all duration-200
                ${child.externalUrl
                  ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 no-underline'
                  : isChildActive
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}
              `;
          if (child.externalUrl) {
            return (
              <a
                key={child.id}
                href={child.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={rowClasses}
              >
                <i className={`${child.icon} w-5 text-center mr-2 opacity-80`} />
                <span className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="truncate">{child.label}</span>
                  {child.id === 'api-docs' && deployEnvLabel ? (
                    <span
                      className="text-[9px] uppercase tracking-wide px-1.5 py-0 rounded bg-slate-200 text-slate-600 shrink-0"
                      title="Deployment / mode (set VITE_APP_ENVIRONMENT in admin build)"
                    >
                      {deployEnvLabel}
                    </span>
                  ) : null}
                </span>
                <i className="fas fa-external-link-alt text-[10px] opacity-60 shrink-0 ml-1" aria-hidden />
              </a>
            );
          }
          return (
            <button
              key={child.id}
              type="button"
              onClick={() => onSelectChild(resolveMenuId(child))}
              className={rowClasses}
            >
              {isChildActive && (
                <div className="absolute left-0 top-1 bottom-1 w-1 bg-white rounded-r-full" />
              )}
              <i className={`${child.icon} w-5 text-center mr-2 opacity-80`} />
              <span className="flex-1 min-w-0 truncate">{child.label}</span>
            </button>
          );
        })}
    </div>
  );
});
MenuGroup.displayName = 'MenuGroup';

// 🎯 Main Sidebar Component
const Sidebar = memo(() => {
  const { sidebar, toggleSidebar, setActiveMenu, currentUser } = useUI();
  const { goToMenu } = useAdminNavigate();
  const navigate = useNavigate();
  const [pendingBookingsBadge, setPendingBookingsBadge] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadPendingBookings = async () => {
      try {
        const res = await ApiService.getBookings({ page: 1, limit: 1, status: 'pending' });
        const total = Number(res?.data?.pagination?.total || 0);
        if (mounted) setPendingBookingsBadge(total > 0 ? String(total) : '');
      } catch (e) {
        if (mounted) setPendingBookingsBadge('');
      }
    };
    loadPendingBookings();
    const timer = setInterval(loadPendingBookings, 30000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  const swaggerDocsHref = useMemo(() => {
    const explicit =
      typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_SWAGGER_URL
        ? String(import.meta.env.VITE_BFF_SWAGGER_URL).trim()
        : '';
    if (explicit) return explicit.replace(/\/$/, '');
    const api =
      typeof import.meta !== 'undefined' && import.meta.env?.VITE_BFF_API_URL
        ? String(import.meta.env.VITE_BFF_API_URL).trim().replace(/\/$/, '')
        : '';
    if (api && /^https?:\/\//i.test(api)) {
      try {
        const u = new URL(api);
        return `${u.origin}/api-docs`;
      } catch {
        /* fallthrough */
      }
    }
    return '/api-docs';
  }, []);

  const menuSections = useMemo(() => {
    const enrich = (item) => {
      if (item.id === 'bookings-group') return { ...item, badge: pendingBookingsBadge };
      if (item.id === 'system-tools-group' && item.children) {
        return {
          ...item,
          children: item.children.map((c) =>
            c.id === 'api-docs' ? { ...c, externalUrl: swaggerDocsHref } : c,
          ),
        };
      }
      return item;
    };
    return filterSidebarSectionsByAcl(
      SIDEBAR_SECTIONS.map((section) => ({
        ...section,
        items: (section.items || []).map(enrich),
      })),
      currentUser,
    );
  }, [pendingBookingsBadge, currentUser, swaggerDocsHref]);

  useEffect(() => {
    const flattened = flattenSidebarMenuIds(menuSections);
    const routeOnlyMenus = ['booking-view', 'booking-edit', 'customer-view', 'customer-edit'];
    const allowedMenus = [...flattened, ...routeOnlyMenus];
    if (allowedMenus.includes(sidebar.activeMenu)) return;
    // Deep-link pages (editors, detail views) are valid routes but not sidebar items.
    if (MENU_TO_PATH[sidebar.activeMenu] && canAccessMenu(currentUser, sidebar.activeMenu)) return;
    const fallback = getFirstAllowedMenu(currentUser, allowedMenus);
    setActiveMenu(fallback);
    navigate(menuIdToPath(fallback), { replace: true });
  }, [currentUser, menuSections, setActiveMenu, sidebar.activeMenu, navigate]);

  return (
    <>
      {/* 📱 Mobile Overlay */}
      {sidebar.isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* 🗂️ Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white text-slate-900 z-50 transition-all duration-300 ease-in-out border-r border-slate-200 shadow-sm
        ${sidebar.isOpen ? 'w-64' : 'w-16'}
        ${sidebar.isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:translate-x-0
        flex flex-col
      `}>
        {/* 📊 Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-200">
          {/* Logo */}
          <div className={`flex items-center transition-all duration-200 ${sidebar.isOpen ? 'space-x-3' : 'justify-center'
            }`}>
            <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center shadow-sm">
              <i className="fas fa-car text-white text-sm"></i>
            </div>

            {sidebar.isOpen && (
              <div className="min-w-0">
                <div className="truncate text-base font-semibold tracking-tight text-slate-900">
                  TejasFleets Car Rental
                </div>
                <div className="truncate text-[11px] font-medium text-slate-500">Admin Panel</div>
              </div>
            )}
          </div>

          {/* Toggle Button - Hidden on desktop when collapsed */}
          <button
            onClick={toggleSidebar}
            className={`
              lg:hidden p-1 rounded-lg hover:bg-slate-100 transition-colors duration-200
              ${!sidebar.isOpen ? 'hidden' : 'block'}
            `}
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* 📑 Navigation Menu */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {menuSections.map((section) => (
            <div key={section.sectionLabel || 'main'} className="space-y-0.5">
              <SectionLabel label={section.sectionLabel} isCollapsed={!sidebar.isOpen} />
              {section.items.map((item) =>
                item.isGroup && item.children?.length ? (
                  <MenuGroup
                    key={item.id}
                    group={item}
                    activeMenu={sidebar.activeMenu}
                    isCollapsed={!sidebar.isOpen}
                    onSelectChild={goToMenu}
                  />
                ) : (
                  <MenuItem
                    key={item.id}
                    item={item}
                    isActive={
                      sidebar.activeMenu === item.id ||
                      sidebar.activeMenu === resolveMenuId(item)
                    }
                    isCollapsed={!sidebar.isOpen}
                    onClick={goToMenu}
                  />
                ),
              )}
            </div>
          ))}
        </nav>

        {/* Footer + collapse */}
        <div className="hidden lg:block border-t border-slate-200">
          {sidebar.isOpen ? (
            <p className="px-4 py-2 text-[10px] font-medium text-slate-400">Version 2.0.0</p>
          ) : null}
          <button
            onClick={toggleSidebar}
            className="flex w-full items-center justify-center gap-2 p-3 text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-900"
            title={sidebar.isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <i className={`fas fa-chevron-${sidebar.isOpen ? 'left' : 'right'}`} aria-hidden />
            {sidebar.isOpen ? <span className="text-xs font-medium">Collapse</span> : null}
          </button>
        </div>
      </div>
    </>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;